import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Sparkle, PencilSimple, Trash, ArrowsLeftRight, CalendarCheck } from '@phosphor-icons/react';
import { LeadFormCard } from '../components/admissions/LeadFormCard';
import { FunnelPanel } from '../components/admissions/FunnelPanel';
import { TasksPanel } from '../components/admissions/TasksPanel';
import { CampaignsPanel } from '../components/admissions/CampaignsPanel';

const STAGES = ['inquiry', 'applied', 'interview', 'offered', 'waitlisted', 'enrolled', 'declined'];
const NEXT = { inquiry: ['applied'], applied: ['interview', 'waitlisted'], interview: ['offered', 'waitlisted', 'declined'], offered: ['declined'], waitlisted: ['offered', 'declined'] };
const ADM_STAFF = ['global_admin', 'school_admin', 'principal', 'admissions_officer', 'registrar'];
const QUEUE_MANAGERS = ['global_admin', 'school_admin', 'principal', 'registrar'];

export default function Admissions() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { t } = useI18n();
  const { data: apps } = useQuery({ queryKey: ['apps'], queryFn: () => api.get('/admissions/applications').then((r) => r.data) });
  const { data: tenants } = useQuery({ queryKey: ['tenants'], queryFn: () => api.get('/tenants').then((r) => r.data) });
  const { data: contacts } = useQuery({ queryKey: ['chat-contacts'], queryFn: () => api.get('/chat/contacts').then((r) => r.data) });
  const [form, setForm] = useState({ applicant_name: '', grade_applied: '5', guardian_name: '', guardian_email: '', notes: '' });
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [editApp, setEditApp] = useState(null);
  const [tq, setTq] = useState({ from: '', to: '' });
  const school = (tenants || []).find((t) => t.type === 'school');
  const admStaff = (contacts || []).filter((c) => ADM_STAFF.includes(c.role));
  const canManageQueue = QUEUE_MANAGERS.includes(user?.role);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['apps'] });
  const stageMut = useMutation({
    mutationFn: ({ id, stage }) => api.patch(`/admissions/applications/${id}/stage`, { stage }),
    onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)),
  });
  const enrollMut = useMutation({
    mutationFn: (id) => api.post(`/admissions/applications/${id}/enroll`),
    onSuccess: (r) => { toast.success(`Enrolled! Student ${r.data.admission_no} created with guardian + consents in one transaction.`); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const createMut = useMutation({
    mutationFn: () => api.post('/admissions/applications', { ...form, tenant_id: school?.id }),
    onSuccess: () => { toast.success('Inquiry created'); setOpen(false); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const aiMut = useMutation({
    mutationFn: (id) => api.post(`/admissions/applications/${id}/ai-summary`),
    onSuccess: (r) => setSummary(r.data), onError: (e) => toast.error(errMsg(e)),
  });
  const editMut = useMutation({
    mutationFn: () => api.patch(`/admissions/applications/${editApp.id}`, {
      applicant_name: editApp.applicant_name, grade_applied: editApp.grade_applied,
      guardian_name: editApp.guardian_name, guardian_email: editApp.guardian_email, notes: editApp.notes || '',
    }),
    onSuccess: () => { toast.success('Application updated'); setEditApp(null); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const withdrawMut = useMutation({
    mutationFn: ({ id, reason }) => api.delete(`/admissions/applications/${id}`, { params: { reason } }),
    onSuccess: () => { toast.success('Application withdrawn'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const assignMut = useMutation({
    mutationFn: ({ id, to }) => api.post(`/admissions/applications/${id}/assign`, { to_user_id: to }),
    onSuccess: (r) => { toast.success(`Assigned to ${r.data.assigned_to_name}`); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const transferQueueMut = useMutation({
    mutationFn: () => api.post('/admissions/transfer-queue', { to_user_id: tq.to, from_user_id: tq.from || null }),
    onSuccess: (r) => { toast.success(`${r.data.transferred} open application(s) transferred to ${r.data.assigned_to_name}`); setTq({ from: '', to: '' }); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const taskMut = useMutation({
    mutationFn: (body) => api.post('/admissions/tasks', body),
    onSuccess: () => { toast.success('Follow-up task created'); qc.invalidateQueries({ queryKey: ['adm-tasks'] }); qc.invalidateQueries({ queryKey: ['adm-analytics'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const addTask = (a) => {
    const title = window.prompt(`Follow-up for ${a.applicant_name} (e.g. Call guardian):`); if (!title) return;
    const due = window.prompt('Due date YYYY-MM-DD (optional):') || null;
    taskMut.mutate({ application_id: a.id, title, due_date: due });
  };

  return (
    <div data-testid="admissions-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t('Admissions')}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-application-button" className="bg-slate-900 hover:bg-slate-800">{t('New inquiry')}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">{t('New application inquiry')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="app-name-input" placeholder="Applicant name" value={form.applicant_name} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} />
              <select data-testid="app-grade-input" value={form.grade_applied} onChange={(e) => setForm({ ...form, grade_applied: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                {['Nursery', 'LKG', 'UKG', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map((g) => <option key={g} value={g}>{/^\d+$/.test(g) ? `Grade ${g}` : g}</option>)}
              </select>
              <Input data-testid="app-guardian-name-input" placeholder="Guardian name" value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} />
              <Input data-testid="app-guardian-email-input" placeholder="Guardian email" value={form.guardian_email} onChange={(e) => setForm({ ...form, guardian_email: e.target.value })} />
              <Input data-testid="app-notes-input" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <Button data-testid="app-submit-button" className="w-full bg-slate-900 hover:bg-slate-800" onClick={() => createMut.mutate()}>{t('Create')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {canManageQueue && (
        <div className="bg-white border border-slate-200 rounded-md p-3 mb-5 flex flex-wrap gap-2 items-center" data-testid="transfer-queue-bar">
          <ArrowsLeftRight size={16} className="text-orange-600" />
          <span className="font-mono-label text-[10px] uppercase text-slate-400">Transfer the queue</span>
          <select data-testid="transfer-queue-from" value={tq.from} onChange={(e) => setTq({ ...tq, from: e.target.value })}
            className="border border-slate-200 rounded-md px-2 py-1.5 text-xs bg-white">
            <option value="">All open applications</option>
            {admStaff.map((c) => <option key={c.id} value={c.id}>{`from ${c.name || c.email}`}</option>)}
          </select>
          <span className="text-slate-400 text-xs">-</span>
          <select data-testid="transfer-queue-to" value={tq.to} onChange={(e) => setTq({ ...tq, to: e.target.value })}
            className="border border-slate-200 rounded-md px-2 py-1.5 text-xs bg-white">
            <option value="">Select new owner...</option>
            {admStaff.map((c) => <option key={c.id} value={c.id}>{`${c.name || c.email} - ${c.role.replace(/_/g, ' ')}`}</option>)}
          </select>
          <Button data-testid="transfer-queue-button" size="sm" variant="outline" disabled={!tq.to || transferQueueMut.isPending}
            onClick={() => { if (window.confirm('Reassign these open applications? The new owner is notified.')) transferQueueMut.mutate(); }}>
            Transfer
          </Button>
        </div>
      )}

      <FunnelPanel />
      <CampaignsPanel />
      <div className="grid lg:grid-cols-2 gap-4 mb-5">
        <LeadFormCard />
        <TasksPanel />
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="admissions-kanban">
        {STAGES.map((stage) => {
          const col = (apps || []).filter((a) => a.stage === stage);
          return (
            <div key={stage} className="min-w-[240px] w-60 shrink-0">
              <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">{t(stage)} · {col.length}</p>
              <div className="space-y-2">
                {col.map((a) => (
                  <div key={a.id} data-testid={`app-card-${a.id}`} className="bg-white border border-slate-200 rounded-md p-3 card-hover stagger-in">
                    <div className="flex items-start justify-between gap-1">
                      <div>
                        <p className="font-semibold text-sm">{a.applicant_name}</p>
                        <p className="text-xs text-slate-500">Grade {a.grade_applied} · {a.guardian_name}</p>
                      </div>
                      {!['enrolled', 'declined'].includes(stage) && (
                        <div className="flex gap-0.5 shrink-0">
                          <button data-testid={`app-task-${a.id}`} title="Add follow-up task" onClick={() => addTask(a)}
                            className="text-slate-400 hover:text-orange-600 p-0.5"><CalendarCheck size={13} /></button>
                          <button data-testid={`app-edit-${a.id}`} title="Edit application" onClick={() => setEditApp({ ...a })}
                            className="text-slate-400 hover:text-slate-900 p-0.5"><PencilSimple size={13} /></button>
                          <button data-testid={`app-withdraw-${a.id}`} title="Withdraw application"
                            onClick={() => { const r = window.prompt('Withdraw this application? Reason:'); if (r !== null) withdrawMut.mutate({ id: a.id, reason: r }); }}
                            className="text-slate-400 hover:text-rose-600 p-0.5"><Trash size={13} /></button>
                        </div>
                      )}
                    </div>
                    {a.assigned_to_name && <p className="font-mono-label text-[9px] uppercase text-orange-700 mt-1" data-testid={`app-assignee-${a.id}`}>owner: {a.assigned_to_name}</p>}
                    {a.source === 'public_form' && <p className="font-mono-label text-[9px] uppercase text-sky-700 mt-1" data-testid={`app-source-${a.id}`}>web lead{a.utm_campaign ? ` · ${a.utm_campaign}` : ''}</p>}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(NEXT[stage] || []).map((nxt) => (
                        <button key={nxt} data-testid={`app-move-${nxt}-${a.id}`} onClick={() => stageMut.mutate({ id: a.id, stage: nxt })}
                          className="text-[10px] font-mono-label uppercase border border-slate-200 rounded px-1.5 py-0.5 hover:border-slate-900 transition-colors">- {t(nxt)}</button>
                      ))}
                      {stage === 'offered' && (
                        <button data-testid={`app-enroll-${a.id}`} onClick={() => enrollMut.mutate(a.id)}
                          className="text-[10px] font-mono-label uppercase bg-green-700 text-white rounded px-1.5 py-0.5 hover:bg-green-600 transition-colors">{t('Enroll')}</button>
                      )}
                      <button data-testid={`app-ai-summary-${a.id}`} onClick={() => aiMut.mutate(a.id)}
                        className="text-[10px] font-mono-label uppercase text-blue-600 border border-blue-200 rounded px-1.5 py-0.5 hover:border-blue-600 transition-colors flex items-center gap-1">
                        <Sparkle size={10} weight="fill" /> AI
                      </button>
                    </div>
                    {!['enrolled', 'declined'].includes(stage) && admStaff.length > 0 && (
                      <select data-testid={`app-assign-select-${a.id}`} value="" onChange={(e) => e.target.value && assignMut.mutate({ id: a.id, to: e.target.value })}
                        className="mt-2 w-full border border-slate-200 rounded px-1.5 py-1 text-[10px] bg-white text-slate-500">
                        <option value="">Assign to...</option>
                        {admStaff.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editApp} onOpenChange={() => setEditApp(null)}>
        <DialogContent data-testid="app-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit application</DialogTitle></DialogHeader>
          {editApp && (
            <div className="space-y-3">
              <Input data-testid="edit-name-input" placeholder="Applicant name" value={editApp.applicant_name} onChange={(e) => setEditApp({ ...editApp, applicant_name: e.target.value })} />
              <Input data-testid="edit-grade-input" placeholder="Grade applied" value={editApp.grade_applied} onChange={(e) => setEditApp({ ...editApp, grade_applied: e.target.value })} />
              <Input data-testid="edit-guardian-name-input" placeholder="Guardian name" value={editApp.guardian_name} onChange={(e) => setEditApp({ ...editApp, guardian_name: e.target.value })} />
              <Input data-testid="edit-guardian-email-input" placeholder="Guardian email" value={editApp.guardian_email} onChange={(e) => setEditApp({ ...editApp, guardian_email: e.target.value })} />
              <Input data-testid="edit-notes-input" placeholder="Notes" value={editApp.notes || ''} onChange={(e) => setEditApp({ ...editApp, notes: e.target.value })} />
              <Button data-testid="edit-save-button" className="w-full bg-slate-900 hover:bg-slate-800" disabled={editMut.isPending} onClick={() => editMut.mutate()}>Save changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!summary} onOpenChange={() => setSummary(null)}>
        <DialogContent data-testid="ai-summary-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkle className="text-blue-600" weight="fill" /> AI summary (advisory)</DialogTitle></DialogHeader>
          <p className="text-sm whitespace-pre-wrap">{summary?.summary}</p>
          <p className="font-mono-label text-[10px] uppercase text-slate-400">{summary?.model} · {summary?.note}</p>
        </DialogContent>
      </Dialog>
      {aiMut.isPending && <p className="font-mono-label text-xs uppercase text-blue-600 mt-2">Generating AI summary...</p>}
    </div>
  );
}
